import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { PrepareUserLoginDocument } from "~/features/admin/users/gqls/mutation/prepareUserLogin.generated";
import { translateError } from "~/lib/errors";

export function useRetryUserLoginPreparation(onPrepared?: () => Promise<unknown>) {
  const { t } = useTranslation();
  const [prepare, { loading }] = useMutation(PrepareUserLoginDocument);
  const retry = useCallback(
    async function retrySavedUser(id: string): Promise<boolean> {
      try {
        await prepare({ variables: { id } });
        await onPrepared?.();
        toast.success(t("feature.users.loginPreparationSucceeded"), { id: `user-login-${id}` });
        return true;
      } catch (error) {
        toast.error(translateError(t, error), {
          id: `user-login-${id}`,
          duration: Number.POSITIVE_INFINITY,
          action: {
            label: t("feature.users.retryLoginPreparation"),
            onClick: () => {
              void retrySavedUser(id);
            },
          },
        });
        return false;
      }
    },
    [onPrepared, prepare, t],
  );
  const notifySaved = useCallback(
    (id: string) => {
      toast.error(t("feature.users.loginPreparationFailed"), {
        id: `user-login-${id}`,
        duration: Number.POSITIVE_INFINITY,
        action: {
          label: t("feature.users.retryLoginPreparation"),
          onClick: () => {
            void retry(id);
          },
        },
      });
    },
    [retry, t],
  );

  return { retry, notifySaved, processing: loading };
}
